import type { ApiClient } from '../../../shared/api/api-client';
import type {
  YcAdminSyncEventsResponse,
  YcAdminSyncStatus,
  YcBookStaffOption,
  YcBranchSettingsSnapshot,
  YcBranchSyncView,
  YcResourceStaffRow,
  YcServiceOption,
  YClientsCreateBranchesFromYCResponse,
  YClientsCreateBranchesFromYCResultRow,
  YClientsDiscoveredCompanyRow,
  YcSyncConflictResolveBody,
  YcSyncLogEntry,
  YcUnmappedItem,
  YClientsUnmappedItemsCountByStatus,
  YClientsUnmappedBulkIgnoreRequest,
  YClientsUnmappedBulkIgnoreResponse,
  YClientsUnmappedBulkIgnoreSkipReason,
  YClientsUnmappedBulkCreateAndMapRequest,
  YClientsUnmappedBulkCreateAndMapResponse,
  YcValidateCredentialsResponse
} from './yclients-sync.types';

function numFromUnknown(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function strFromUnknown(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function objFromUnknown(v: unknown): Record<string, unknown> | null {
  if (v != null && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

/**
 * Нормализует строку журнала YC sync (snake_case после json-тегов;
 * на всякий случай принимает PascalCase от старых сборок).
 */
export function normalizeYcSyncLogEntry(raw: unknown): YcSyncLogEntry | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = numFromUnknown(o.id ?? o.ID);
  if (id == null) return null;
  const createdAt =
    strFromUnknown(o.created_at_utc ?? o.CreatedAtUtc ?? o.created_at ?? o.CreatedAt) ?? '';
  const orgId = numFromUnknown(o.organization_id ?? o.OrganizationID);
  const branchId = numFromUnknown(o.branch_id ?? o.BranchID);
  const source = strFromUnknown(o.source ?? o.Source) ?? '';
  const direction = strFromUnknown(o.direction ?? o.Direction);
  const eventType = strFromUnknown(o.event_type ?? o.EventType) ?? '';
  const status = strFromUnknown(o.status ?? o.Status) ?? '';
  const severity = strFromUnknown(o.severity ?? o.Severity) ?? '';
  const durationMs = numFromUnknown(o.duration_ms ?? o.DurationMs);
  const message = strFromUnknown(o.message ?? o.Message);
  const details = objFromUnknown(o.details ?? o.Details);
  const conflictData = objFromUnknown(o.conflict_data ?? o.ConflictData);
  const conflictResolution = strFromUnknown(o.conflict_resolution ?? o.ConflictResolution);
  const resolvedBy = numFromUnknown(o.conflict_resolved_by_user_id ?? o.ConflictResolvedByUserID);
  const resolvedAt = strFromUnknown(o.conflict_resolved_at_utc ?? o.ConflictResolvedAtUtc);
  const conflictSummary = strFromUnknown(o.conflict_summary ?? o.ConflictSummary);
  return {
    id,
    created_at_utc: createdAt,
    organization_id: orgId,
    branch_id: branchId,
    source,
    direction,
    event_type: eventType,
    status,
    severity,
    duration_ms: durationMs,
    message,
    details,
    conflict_data: conflictData,
    conflict_resolution: conflictResolution,
    conflict_resolved_by_user_id: resolvedBy,
    conflict_resolved_at_utc: resolvedAt,
    conflict_summary: conflictSummary ?? null
  };
}

export async function fetchYcAdminSyncStatus(client: ApiClient): Promise<YcAdminSyncStatus> {
  return client.request<YcAdminSyncStatus>('/admin/yc-sync/status', { method: 'GET' });
}

export type YcAdminSyncEventsQuery = {
  limit?: number;
  offset?: number;
  branch_id?: number;
  only_conflicts?: boolean;
  unresolved_only?: boolean;
  source?: string;
  direction?: 'yc_to_crm' | 'crm_to_yc' | 'bidirectional';
  event_type?: string;
  status?: string;
  severity?: string;
  min_duration_ms?: number;
  max_duration_ms?: number;
};

export async function fetchYcAdminSyncEvents(
  client: ApiClient,
  query: YcAdminSyncEventsQuery = {}
): Promise<YcAdminSyncEventsResponse> {
  const params = new URLSearchParams();
  const limRaw = query.limit;
  const lim =
    typeof limRaw === 'number' && Number.isFinite(limRaw)
      ? Math.min(100, Math.max(1, Math.floor(limRaw)))
      : 50;
  params.set('limit', String(lim));
  const off =
    typeof query.offset === 'number' && Number.isFinite(query.offset) && query.offset >= 0
      ? Math.floor(query.offset)
      : 0;
  params.set('offset', String(off));
  if (query.branch_id != null && query.branch_id > 0) {
    params.set('branch_id', String(query.branch_id));
  }
  if (query.only_conflicts === true) params.set('only_conflicts', 'true');
  if (query.unresolved_only === true) params.set('unresolved_only', 'true');
  if (query.source?.trim()) params.set('source', query.source.trim());
  if (query.direction) params.set('direction', query.direction);
  if (query.event_type?.trim()) params.set('event_type', query.event_type.trim());
  if (query.status?.trim()) params.set('status', query.status.trim());
  if (query.severity?.trim()) params.set('severity', query.severity.trim());
  if (typeof query.min_duration_ms === 'number' && query.min_duration_ms >= 0) {
    params.set('min_duration_ms', String(Math.floor(query.min_duration_ms)));
  }
  if (typeof query.max_duration_ms === 'number' && query.max_duration_ms >= 0) {
    params.set('max_duration_ms', String(Math.floor(query.max_duration_ms)));
  }

  const qs = params.toString();
  const data = await client.request<unknown>(`/admin/yc-sync/events?${qs}`, { method: 'GET' });
  if (Array.isArray(data)) {
    const items = data.map(normalizeYcSyncLogEntry).filter((x): x is YcSyncLogEntry => x != null);
    return { items, total: items.length };
  }
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    const rawItems = (data as { items: unknown[] }).items;
    const items = rawItems.map(normalizeYcSyncLogEntry).filter((x): x is YcSyncLogEntry => x != null);
    const total =
      typeof (data as { total?: unknown }).total === 'number'
        ? Math.max(0, (data as { total: number }).total)
        : items.length;
    return { items, total };
  }
  return { items: [], total: 0 };
}

export async function postYcSyncConflictResolve(
  client: ApiClient,
  entryId: number,
  body: YcSyncConflictResolveBody
): Promise<YcSyncLogEntry> {
  const payload: Record<string, unknown> = { action: body.action };
  if (body.note != null && String(body.note).trim() !== '') {
    payload.note = String(body.note).trim();
  }
  const raw = await client.request<unknown>(`/admin/yc-sync/conflicts/${entryId}/resolve`, {
    method: 'POST',
    body: payload
  });
  const parsed = normalizeYcSyncLogEntry(raw);
  if (!parsed) {
    throw new Error('unexpected_resolve_response');
  }
  return parsed;
}

export async function fetchYcBranchSyncView(client: ApiClient, branchId: number): Promise<YcBranchSyncView> {
  return client.request<YcBranchSyncView>(`/organizations/branches/${branchId}/yclients/sync`, {
    method: 'GET'
  });
}

export async function postYcBranchImport(
  client: ApiClient,
  branchId: number,
  idempotencyKey?: string | null
): Promise<{ outbox_id: number; duplicate: boolean }> {
  const body =
    idempotencyKey != null && idempotencyKey.trim() !== ''
      ? { idempotency_key: idempotencyKey.trim() }
      : {};
  return client.request<{ outbox_id: number; duplicate: boolean }>(
    `/organizations/branches/${branchId}/yclients/import`,
    { method: 'POST', body }
  );
}

export async function postYcBranchBookStaff(
  client: ApiClient,
  branchId: number,
  idempotencyKey?: string | null
): Promise<{ outbox_id: number; duplicate: boolean }> {
  const body =
    idempotencyKey != null && idempotencyKey.trim() !== ''
      ? { idempotency_key: idempotencyKey.trim() }
      : {};
  return client.request<{ outbox_id: number; duplicate: boolean }>(
    `/organizations/branches/${branchId}/yclients/book-staff`,
    { method: 'POST', body }
  );
}

export async function postOrgYClientsValidate(
  client: ApiClient,
  body: Record<string, unknown>
): Promise<YcValidateCredentialsResponse> {
  return client.request<YcValidateCredentialsResponse>('/organizations/me/yclients/validate', {
    method: 'POST',
    body
  });
}

function strFromYCCompanyField(v: unknown): string | null {
  return strFromUnknown(v);
}

/**
 * Нормализует ответ POST /organizations/me/yclients/discover-branches.
 */
export function parseYClientsDiscoverBranchesResponse(raw: unknown): {
  items: YClientsDiscoveredCompanyRow[];
  total: number;
} {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!o) return { items: [], total: 0 };
  const total = typeof o.total === 'number' && Number.isFinite(o.total) ? Math.max(0, Math.floor(o.total)) : 0;
  const arr = o.items;
  if (!Array.isArray(arr)) return { items: [], total };
  const items: YClientsDiscoveredCompanyRow[] = [];
  for (const row of arr) {
    if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
    const r = row as Record<string, unknown>;
    const ycId = strFromUnknown(r.yc_company_id);
    const title = strFromUnknown(r.title) ?? '';
    if (!ycId || !title) continue;
    const linkedId = numFromUnknown(r.linked_branch_id);
    items.push({
      yc_company_id: ycId,
      title,
      public_title: strFromYCCompanyField(r.public_title),
      address: strFromYCCompanyField(r.address),
      phone: strFromYCCompanyField(r.phone),
      country: strFromYCCompanyField(r.country),
      city: strFromYCCompanyField(r.city),
      linked_branch_id: linkedId != null && linkedId > 0 ? linkedId : null,
      linked_branch_name: strFromYCCompanyField(r.linked_branch_name)
    });
  }
  return { items, total };
}

export async function postOrgMeYClientsDiscoverBranches(
  client: ApiClient,
  body: Record<string, unknown>
): Promise<{ items: YClientsDiscoveredCompanyRow[]; total: number }> {
  const raw = await client.request<unknown>('/organizations/me/yclients/discover-branches', {
    method: 'POST',
    body
  });
  return parseYClientsDiscoverBranchesResponse(raw);
}

function parseYClientsCreateBranchesResultRow(raw: unknown): YClientsCreateBranchesFromYCResultRow | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const ycId = strFromUnknown(r.yc_company_id);
  if (!ycId) return null;
  const created = Boolean(r.created);
  const bid = numFromUnknown(r.branch_id);
  return {
    yc_company_id: ycId,
    branch_id: bid != null && bid > 0 ? bid : null,
    branch_name: strFromUnknown(r.branch_name),
    created,
    already_linked: Boolean(r.already_linked),
    error: strFromUnknown(r.error)
  };
}

export function parseYClientsCreateBranchesFromYCResponse(raw: unknown): YClientsCreateBranchesFromYCResponse {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!o) {
    return { items: [], created: 0, already_linked: 0, failed: 0 };
  }
  const itemsRaw = o.items;
  const items: YClientsCreateBranchesFromYCResultRow[] = [];
  if (Array.isArray(itemsRaw)) {
    for (const row of itemsRaw) {
      const p = parseYClientsCreateBranchesResultRow(row);
      if (p) items.push(p);
    }
  }
  const created = typeof o.created === 'number' ? Math.max(0, Math.floor(o.created)) : 0;
  const already_linked =
    typeof o.already_linked === 'number' ? Math.max(0, Math.floor(o.already_linked)) : 0;
  const failed = typeof o.failed === 'number' ? Math.max(0, Math.floor(o.failed)) : 0;
  return { items, created, already_linked, failed };
}

export async function postOrgMeYClientsCreateBranchesFromYC(
  client: ApiClient,
  body: Record<string, unknown>
): Promise<YClientsCreateBranchesFromYCResponse> {
  const raw = await client.request<unknown>('/organizations/me/yclients/create-branches-from-yc', {
    method: 'POST',
    body
  });
  return parseYClientsCreateBranchesFromYCResponse(raw);
}

function asValidDirection(v: unknown): 'yc_to_crm' | 'crm_to_yc' | 'bidirectional' {
  const s = String(v ?? '').trim();
  if (s === 'yc_to_crm' || s === 'crm_to_yc' || s === 'bidirectional') return s;
  return 'yc_to_crm';
}

function asResourceStaffRows(v: unknown): YcResourceStaffRow[] {
  if (!Array.isArray(v)) return [];
  const out: YcResourceStaffRow[] = [];
  for (const item of v) {
    if (item == null || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const yc = numFromUnknown(o.yc_staff_id);
    const crm = numFromUnknown(o.crm_service_id);
    if (yc != null && yc > 0 && crm != null && crm > 0) {
      out.push({ yc_staff_id: yc, crm_service_id: crm });
    }
  }
  return out;
}

function maskOrNull(v: unknown): '***' | null {
  return v === '***' ? '***' : null;
}

/**
 * Парсит YClients-настройки филиала из ответа `GET /organizations/branches/{id}` (или элемента списка).
 * Терпим к отсутствующим полям (дефолты совпадают с branchWireDefaults на сервере).
 */
export function parseBranchYclientsSnapshot(raw: unknown): YcBranchSettingsSnapshot {
  const o = (raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {});
  const companyRaw = o.yclients_company_id;
  const companyStr =
    companyRaw == null ? null : String(companyRaw).trim() === '' ? null : String(companyRaw).trim();
  const sched = numFromUnknown(o.yclients_schedule_service_id);
  return {
    yclients_sync_enabled: Boolean(o.yclients_sync_enabled),
    yc_sync_direction: asValidDirection(o.yc_sync_direction),
    yclients_company_id: companyStr,
    yclients_login: strFromUnknown(o.yclients_login),
    yclients_resource_staff: asResourceStaffRows(o.yclients_resource_staff),
    yclients_schedule_service_id: sched != null && sched > 0 ? sched : null,
    yclients_password: maskOrNull(o.yclients_password),
    yclients_partner_token: maskOrNull(o.yclients_partner_token),
    yclients_password_set: Boolean(o.yclients_password_set),
    yclients_partner_token_set: Boolean(o.yclients_partner_token_set),
    yclients_token_set: Boolean(o.yclients_token_set)
  };
}

/**
 * `GET /organizations/branches/{branch_id}` — полная карточка филиала со всеми платёжными
 * и YClients-полями (BranchItem). Возвращаем сырое тело (вышестоящий код парсит то, что нужно).
 */
export async function fetchBranchDetail(client: ApiClient, branchId: number): Promise<unknown> {
  return client.request<unknown>(`/organizations/branches/${branchId}`, { method: 'GET' });
}

export async function fetchBranchYclientsSnapshot(
  client: ApiClient,
  branchId: number
): Promise<YcBranchSettingsSnapshot> {
  const raw = await fetchBranchDetail(client, branchId);
  return parseBranchYclientsSnapshot(raw);
}

/**
 * `PATCH /organizations/branches/{branch_id}` — общий PATCH профиля филиала. Сюда же уходят
 * YClients-поля; пустые секреты не отправляем (это не очищает значение на сервере).
 */
export async function patchBranch(
  client: ApiClient,
  branchId: number,
  body: Record<string, unknown>
): Promise<unknown> {
  return client.request<unknown>(`/organizations/branches/${branchId}`, { method: 'PATCH', body });
}

/** `GET /organizations/branches/{branch_id}/yclients/book-staff` — список «зон»/сотрудников YC. */
export async function getYcBookStaff(client: ApiClient, branchId: number): Promise<YcBookStaffOption[]> {
  const raw = await client.request<unknown>(`/organizations/branches/${branchId}/yclients/book-staff`, {
    method: 'GET'
  });
  if (!Array.isArray(raw)) return [];
  const out: YcBookStaffOption[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const id = numFromUnknown(o.id);
    const name = strFromUnknown(o.name) ?? '';
    if (id != null && id > 0 && name) out.push({ id, name });
  }
  return out;
}

/** `GET /organizations/me/services?branch_id=&active_only=false` — список услуг филиала. */
export async function getServicesForBranch(
  client: ApiClient,
  branchId: number
): Promise<YcServiceOption[]> {
  const params = new URLSearchParams();
  params.set('branch_id', String(branchId));
  params.set('active_only', 'false');
  params.set('limit', '500');
  const raw = await client.request<unknown>(`/organizations/me/services?${params.toString()}`, {
    method: 'GET'
  });
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (raw && typeof raw === 'object' && Array.isArray((raw as { items?: unknown }).items)) {
    arr = (raw as { items: unknown[] }).items;
  }
  const out: YcServiceOption[] = [];
  for (const item of arr) {
    if (item == null || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const id = numFromUnknown(o.id);
    const name = strFromUnknown(o.name) ?? '';
    if (id != null && id > 0 && name) out.push({ id, name });
  }
  return out;
}

export type YcUnmappedItemsQuery = {
  entity_type?: string;
  status?: string;
  branch_id?: number;
  cursor?: string;
  limit?: number;
};

function parseYcUnmappedItem(raw: unknown): YcUnmappedItem | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = numFromUnknown(o.id);
  const orgId = numFromUnknown(o.organization_id);
  const branchId = numFromUnknown(o.branch_id);
  if (id == null || id < 1 || orgId == null || orgId < 1 || branchId == null || branchId < 1) return null;
  const ycCo = strFromUnknown(o.yc_company_id) ?? '';
  const et = strFromUnknown(o.entity_type) ?? '';
  const ycId = strFromUnknown(o.yc_id) ?? '';
  const st = strFromUnknown(o.status) ?? '';
  const createdAt = strFromUnknown(o.created_at) ?? '';
  const updatedAt = strFromUnknown(o.updated_at) ?? '';
  const payload = objFromUnknown(o.payload);
  const resolvedCrm = numFromUnknown(o.resolved_crm_id);
  const note = strFromUnknown(o.resolution_note);
  const resolvedAt = strFromUnknown(o.resolved_at);
  const resolvedBy = numFromUnknown(o.resolved_by_user_id);
  return {
    id,
    organization_id: orgId,
    branch_id: branchId,
    yc_company_id: ycCo,
    entity_type: et,
    yc_id: ycId,
    payload,
    status: st,
    resolved_crm_id: resolvedCrm != null && resolvedCrm > 0 ? resolvedCrm : null,
    resolution_note: note != null && note !== '' ? note : null,
    created_at: createdAt,
    updated_at: updatedAt,
    resolved_at: resolvedAt != null && resolvedAt !== '' ? resolvedAt : null,
    resolved_by_user_id: resolvedBy != null && resolvedBy > 0 ? resolvedBy : null
  };
}

/** GET /organizations/me/yclients/unmapped-items/count — счётчики очереди по статусам. */
export async function fetchYClientsUnmappedItemsCount(
  client: ApiClient
): Promise<{ by_status: YClientsUnmappedItemsCountByStatus }> {
  const raw = await client.request<unknown>('/organizations/me/yclients/unmapped-items/count', {
    method: 'GET'
  });
  const bag = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const bs = bag.by_status != null && typeof bag.by_status === 'object' && !Array.isArray(bag.by_status)
    ? (bag.by_status as Record<string, unknown>)
    : {};
  const open = numFromUnknown(bs.open);
  const resolvedBind = numFromUnknown(bs.resolved_bind);
  const resolvedCreate = numFromUnknown(bs.resolved_create);
  const ignored = numFromUnknown(bs.ignored);
  return {
    by_status: {
      open: open != null && open >= 0 ? open : 0,
      resolved_bind: resolvedBind != null && resolvedBind >= 0 ? resolvedBind : 0,
      resolved_create: resolvedCreate != null && resolvedCreate >= 0 ? resolvedCreate : 0,
      ignored: ignored != null && ignored >= 0 ? ignored : 0
    }
  };
}

/** GET /organizations/me/yclients/unmapped-items — очередь записей импорта без сопоставления с CRM. */
export async function fetchOrgMeYClientsUnmappedItems(
  client: ApiClient,
  query: YcUnmappedItemsQuery = {}
): Promise<{ items: YcUnmappedItem[]; total: number; next_cursor: string }> {
  const params = new URLSearchParams();
  if (query.entity_type != null && query.entity_type.trim() !== '') {
    params.set('entity_type', query.entity_type.trim());
  }
  if (query.status != null && query.status.trim() !== '') {
    params.set('status', query.status.trim());
  }
  if (typeof query.branch_id === 'number' && Number.isFinite(query.branch_id) && query.branch_id > 0) {
    params.set('branch_id', String(Math.floor(query.branch_id)));
  }
  if (query.cursor != null && query.cursor.trim() !== '') {
    params.set('cursor', query.cursor.trim());
  }
  const lim =
    typeof query.limit === 'number' && Number.isFinite(query.limit)
      ? Math.min(200, Math.max(1, Math.floor(query.limit)))
      : 50;
  params.set('limit', String(lim));
  const raw = await client.request<unknown>(
    `/organizations/me/yclients/unmapped-items?${params.toString()}`,
    { method: 'GET' }
  );
  const bag = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const itemsRaw = bag.items;
  const items: YcUnmappedItem[] = [];
  if (Array.isArray(itemsRaw)) {
    for (const row of itemsRaw) {
      const it = parseYcUnmappedItem(row);
      if (it) items.push(it);
    }
  }
  const total = numFromUnknown(bag.total);
  const nextCursor = strFromUnknown(bag.next_cursor) ?? '';
  return {
    items,
    total: total != null && total >= 0 ? total : items.length,
    next_cursor: nextCursor
  };
}

export async function postOrgMeYClientsUnmappedBind(
  client: ApiClient,
  itemId: number,
  body: { crm_id: number }
): Promise<{ ok: boolean }> {
  return client.request<{ ok: boolean }>(`/organizations/me/yclients/unmapped-items/${itemId}/bind`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export async function postOrgMeYClientsUnmappedIgnore(
  client: ApiClient,
  itemId: number,
  body: { note?: string | null }
): Promise<{ ok: boolean }> {
  return client.request<{ ok: boolean }>(`/organizations/me/yclients/unmapped-items/${itemId}/ignore`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export async function postOrgMeYClientsUnmappedCreateAndMap(
  client: ApiClient,
  itemId: number,
  body?: { phone?: string; email?: string }
): Promise<{ ok: boolean; resolved_crm_id?: number }> {
  return client.request<{ ok: boolean; resolved_crm_id?: number }>(
    `/organizations/me/yclients/unmapped-items/${itemId}/create-and-map`,
    {
      method: 'POST',
      body: body || {}
    }
  );
}

const BULK_IGNORE_SKIP_REASONS: ReadonlyArray<YClientsUnmappedBulkIgnoreSkipReason> = [
  'not_found',
  'not_open',
  'branch_forbidden'
];

function asBulkIgnoreSkipReason(v: unknown): YClientsUnmappedBulkIgnoreSkipReason {
  const s = String(v ?? '').trim();
  if ((BULK_IGNORE_SKIP_REASONS as ReadonlyArray<string>).includes(s)) {
    return s as YClientsUnmappedBulkIgnoreSkipReason;
  }
  return 'not_found';
}

function parseBulkIgnoreResponse(raw: unknown): YClientsUnmappedBulkIgnoreResponse {
  const bag = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const processed = numFromUnknown(bag.processed);
  const okIds: number[] = [];
  if (Array.isArray(bag.ok)) {
    for (const row of bag.ok) {
      const n = numFromUnknown(row);
      if (n != null && n > 0) okIds.push(n);
    }
  }
  const skipped: YClientsUnmappedBulkIgnoreResponse['skipped'] = [];
  if (Array.isArray(bag.skipped)) {
    for (const row of bag.skipped) {
      if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
      const r = row as Record<string, unknown>;
      const id = numFromUnknown(r.id);
      if (id == null || id < 1) continue;
      skipped.push({ id, reason: asBulkIgnoreSkipReason(r.reason) });
    }
  }
  return {
    processed: processed != null && processed >= 0 ? processed : okIds.length + skipped.length,
    ok: okIds,
    skipped
  };
}

function parseBulkCreateResponse(raw: unknown): YClientsUnmappedBulkCreateAndMapResponse {
  const bag = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const processed = numFromUnknown(bag.processed);
  const okOut: YClientsUnmappedBulkCreateAndMapResponse['ok'] = [];
  if (Array.isArray(bag.ok)) {
    for (const row of bag.ok) {
      if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
      const r = row as Record<string, unknown>;
      const id = numFromUnknown(r.id);
      const crmId = numFromUnknown(r.resolved_crm_id);
      const et = strFromUnknown(r.entity_type) ?? '';
      if (id == null || id < 1 || crmId == null || crmId < 1 || et === '') continue;
      okOut.push({ id, resolved_crm_id: crmId, entity_type: et });
    }
  }
  const failOut: YClientsUnmappedBulkCreateAndMapResponse['failed'] = [];
  if (Array.isArray(bag.failed)) {
    for (const row of bag.failed) {
      if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
      const r = row as Record<string, unknown>;
      const id = numFromUnknown(r.id);
      const code = strFromUnknown(r.code) ?? '';
      const detail = strFromUnknown(r.detail) ?? '';
      if (id == null || id < 1) continue;
      const et = strFromUnknown(r.entity_type);
      const entry: YClientsUnmappedBulkCreateAndMapResponse['failed'][number] = {
        id,
        code,
        detail
      };
      if (et != null && et !== '') entry.entity_type = et;
      failOut.push(entry);
    }
  }
  return {
    processed: processed != null && processed >= 0 ? processed : okOut.length + failOut.length,
    ok: okOut,
    failed: failOut
  };
}

/** POST /organizations/me/yclients/unmapped-items/bulk-ignore. */
export async function postOrgMeYClientsUnmappedBulkIgnore(
  client: ApiClient,
  body: YClientsUnmappedBulkIgnoreRequest
): Promise<YClientsUnmappedBulkIgnoreResponse> {
  const payload: Record<string, unknown> = { ids: body.ids };
  if (body.note != null && body.note.trim() !== '') {
    payload.note = body.note.trim();
  }
  const raw = await client.request<unknown>(
    '/organizations/me/yclients/unmapped-items/bulk-ignore',
    { method: 'POST', body: payload }
  );
  return parseBulkIgnoreResponse(raw);
}

/** POST /organizations/me/yclients/unmapped-items/bulk-create-and-map. */
export async function postOrgMeYClientsUnmappedBulkCreateAndMap(
  client: ApiClient,
  body: YClientsUnmappedBulkCreateAndMapRequest
): Promise<YClientsUnmappedBulkCreateAndMapResponse> {
  const raw = await client.request<unknown>(
    '/organizations/me/yclients/unmapped-items/bulk-create-and-map',
    { method: 'POST', body: { ids: body.ids } }
  );
  return parseBulkCreateResponse(raw);
}
